import { useState } from "react";

/**
 * A project's own icon, taken from its homepage.
 *
 * The URL carries the content hash, so the browser fetches each icon once and
 * keeps it — which is what makes this affordable on a table that polls every few
 * seconds.
 *
 * A project without an icon falls back to its initial rather than to a generic
 * placeholder: in a list of thirty rows, a distinct letter is still a landmark,
 * whereas the same grey square repeated thirty times is noise.
 *
 * `framed` sits the icon on a white tile with a hairline ring. Favicons are drawn
 * to sit on a page, so a transparent one floats on a coloured surface and a dark
 * one disappears in dark mode; the tile gives every project the same ground and
 * makes a row of them line up.
 *
 * The icon fills the tile rather than sitting inset in it. An inset makes the
 * frame the thing you see and leaves the icon — the only part carrying any
 * information — smaller than the space it was given. `object-contain` still keeps
 * a non-square icon in proportion; it just reaches the edges now.
 */
export function ProjectIcon({ slug, hash, src, size = 18, framed = false, className = "" }) {
    const [failed, setFailed] = useState(false);

    if (!slug) {
        return <span className={`inline-block shrink-0 ${className}`} style={{ width: size, height: size }} aria-hidden />;
    }

    const box = { width: size, height: size };

    const glyph =
        hash && !failed ? (
            <img
                // The live board passes its own: an <img> cannot carry a screen key.
                src={src ?? `/api/projects/${slug}/favicon?v=${hash}`}
                alt=""
                width={size}
                height={size}
                loading="lazy"
                decoding="async"
                onError={() => setFailed(true)}
                className={`shrink-0 object-contain ${framed ? "h-full w-full" : `rounded-sm ${className}`}`}
                style={framed ? undefined : box}
            />
        ) : (
            <span
                className={`inline-flex shrink-0 items-center justify-center font-medium uppercase ${
                    framed ? "h-full w-full text-gray-400 dark:text-gray-500" : "rounded-sm bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400"
                } ${framed ? "" : className}`}
                // A letter fills a tile differently from a glyph: proportionally
                // smaller, or it crowds the ring.
                style={{ ...(framed ? {} : box), fontSize: Math.round(size * (framed ? 0.42 : 0.62)) }}
                aria-hidden
                title={slug}
            >
                {slug[0]}
            </span>
        );

    if (!framed) return glyph;

    return (
        <span
            className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white ring-1 ring-gray-200 dark:bg-gray-800 dark:ring-gray-700 ${className}`}
            style={{ width: size, height: size }}
        >
            {glyph}
        </span>
    );
}
